import { cloneTemplate, setCompanyLogo, text } from './dom'
import type { Workspace, PreviewActions } from './workspace'

export function createInbox(root: HTMLElement, workspace: Workspace, actions: PreviewActions) {
  const page = root.querySelector<HTMLElement>('[data-marketing-page="inbox"]')!
  const form = page.querySelector<HTMLFormElement>('[data-preview-inbox-reply-form]')!
  const input = form.elements.namedItem('reply') as HTMLTextAreaElement
  const drafts = new Map<string, string>()
  let category = 'applications'
  let mailbox = 'all'
  let done = false
  let selected = workspace.conversations[0].id

  function render() {
    page
      .querySelectorAll<HTMLElement>('[data-preview-mailbox]')
      .forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.previewMailbox === mailbox)))
    page
      .querySelectorAll<HTMLElement>('[data-preview-inbox-category]')
      .forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.previewInboxCategory === category))
      )
    page.querySelectorAll<HTMLElement>('[data-preview-inbox-count]').forEach((count) => {
      count.textContent = String(
        workspace.conversations.filter(
          (item) =>
            item.category === count.dataset.previewInboxCount &&
            item.done === done &&
            (mailbox === 'all' || item.account === mailbox)
        ).length
      )
    })
    page.querySelector('[data-preview-inbox-done]')!.setAttribute('aria-pressed', String(done))
    const list = workspace.conversations.filter(
      (item) => item.category === category && item.done === done && (mailbox === 'all' || item.account === mailbox)
    )
    if (!list.some((item) => item.id === selected)) selected = list[0]?.id ?? ''
    page.querySelector<HTMLElement>('[data-preview-inbox-empty]')!.hidden = list.length > 0
    page.querySelector('[data-preview-inbox-list]')!.replaceChildren(
      ...list.map((item) => {
        const row = cloneTemplate(root, 'conversation-row')
        row.dataset.previewConversation = item.id
        row.setAttribute('aria-pressed', String(item.id === selected))
        setCompanyLogo(root, row.querySelector<HTMLElement>('[data-preview-logo]')!, item.company)
        for (const field of ['company', 'role', 'body', 'category'] as const) text(row, field, item[field])
        return row
      })
    )
    const conversation = list.find((item) => item.id === selected)
    page.querySelector<HTMLElement>('[data-preview-inbox-conversation]')!.hidden = !conversation
    page.querySelector<HTMLElement>('[data-preview-inbox-select]')!.hidden = Boolean(conversation)
    if (!conversation) return
    for (const [field, value] of Object.entries({
      subject: conversation.subject,
      company: conversation.company,
      initial: conversation.sender[0],
      sender: conversation.sender,
      body: conversation.body,
      signature: conversation.sender
    }))
      page.querySelector(`[data-preview-inbox-${field}]`)!.textContent = value
    page.querySelector('[data-preview-inbox-replies]')!.replaceChildren(
      ...conversation.replies.map((reply) => {
        const paragraph = document.createElement('p')
        paragraph.textContent = `You: ${reply}`
        return paragraph
      })
    )
    input.value = drafts.get(selected) ?? ''
    page.querySelector('[data-preview-close-conversation]')!.textContent = done
      ? 'Reopen conversation'
      : 'Close conversation'
    form.hidden = done
  }

  page.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('button')
    if (!button) return
    if (button.dataset.previewMailbox) {
      mailbox = button.dataset.previewMailbox
      render()
    }
    if (button.dataset.previewInboxCategory) {
      category = button.dataset.previewInboxCategory
      render()
    }
    if (button.dataset.previewConversation) {
      selected = button.dataset.previewConversation
      render()
    }
    if (button.hasAttribute('data-preview-inbox-done')) {
      done = !done
      render()
    }
    if (button.hasAttribute('data-preview-close-conversation')) {
      workspace.conversations.find((item) => item.id === selected)!.done = !done
      render()
      actions.report(done ? 'Conversation reopened.' : 'Conversation closed.')
    }
    if (button.hasAttribute('data-preview-inbox-refresh')) {
      button.textContent = 'Mail checked just now ↻'
      actions.report('Your inbox is up to date.')
    }
  })
  input.addEventListener('input', () => {
    drafts.set(selected, input.value)
    page.querySelector('[data-preview-inbox-draft-status]')!.textContent = input.value ? 'Draft saved' : 'Draft'
  })
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const reply = input.value.trim()
    if (!reply) return
    workspace.conversations.find((item) => item.id === selected)!.replies.push(reply)
    drafts.delete(selected)
    render()
    actions.report('Reply added to the conversation.')
  })
  return { render }
}
